import { supabase } from "../config/supabase";
import { renderMenu } from "./shared.js";
import { clear } from "./helper.js";
import {
  controlNewPlan,
  controlNewPlanTest,
  controlOpenPlan,
  controlCompany,
  controlProduct,
  controlFertiProduct,
  controlCrop,
  controlSector,
} from "./controller.js";
import { auth as authService } from "./services/auth";

export class Auth {
  constructor() {
    this.logoutStatusCheck = false;
    this.currentAccount = null;

    // Bind methods to preserve 'this' context
    this.handleSignedIn = this.handleSignedIn.bind(this);
    this.handleSignedOut = this.handleSignedOut.bind(this);
    this.checkInitialAuthState = this.checkInitialAuthState.bind(this);
    this.setupAccountModal = this.setupAccountModal.bind(this);

    // Listen for auth state changes
    supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN") {
        this.handleSignedIn(session.user);
      } else if (event === "SIGNED_OUT") {
        this.handleSignedOut();
      }
    });
  }

  async signIn(email, password) {
    try {
      const { error } = await authService.signIn(email, password);

      if (error) throw error;

      const { user, userProfile } = await authService.getCurrentUserWithRole();
      this.currentAccount = {
        ...user,
        status: userProfile.status,
        ...userProfile,
      };

      return {
        success: true,
        user,
      };
    } catch (error) {
      console.error("Sign in error:", error.message);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  async signOut() {
    try {
      // Show confirmation dialog based on logoutStatusCheck
      if (this.logoutStatusCheck) {
        this.renderLogoutWindow(
          "Sei sicuro di voler uscire? Le modifiche non verranno salvate. Per salvare i dati inseriti, compilare il piano fino alla fine, prima di uscire."
        );
      } else {
        this.renderLogoutWindow("Sei sicuro di voler uscire?");
      }

      // Return early - actual logout will be handled by confirmation
      return { success: true };
    } catch (error) {
      console.error("Sign out error:", error.message);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  setLogoutStatus(status) {
    this.logoutStatusCheck = status;
  }

  // Private methods to handle auth state changes
  async handleSignedIn(user) {
    const { userProfile } = await authService.getCurrentUserWithRole();
    this.currentAccount = {
      ...user,
      status: userProfile.status,
      ...userProfile,
    };

    // Emit custom event for the new UI handler
    const loginEvent = new CustomEvent("userLoggedIn", {
      detail: { user: this.currentAccount },
    });
    document.dispatchEvent(loginEvent);

    // Legacy UI updates for existing functionality
    const labelWelcome = document.querySelector(".welcome");
    const containerApp = document.querySelector(".app");
    const btnLogout = document.querySelector(".logout__btn");
    const btnHelp = document.querySelector("#help-btn");
    const btnAccount = document.querySelector("#account-btn");
    const loginUser = document.querySelector(".login__input--user");
    const loginPwd = document.querySelector(".login__input--pin");
    const btnLogin = document.querySelector(".login__btn");
    const menuTag = document.querySelector(".menu-tag");

    // Legacy updates for existing code compatibility
    if (labelWelcome) labelWelcome.textContent = `Buon lavoro, ${user.email}`;
    if (containerApp) containerApp.style.opacity = 100;
    if (btnLogout) btnLogout.classList.remove("hidden");
    if (btnHelp) btnHelp.classList.remove("hidden");
    if (btnAccount) btnAccount.classList.remove("hidden");
    if (loginUser) loginUser.classList.add("hidden");
    if (loginPwd) loginPwd.classList.add("hidden");
    if (btnLogin) btnLogin.classList.add("hidden");

    // Clear and setup menu
    if (menuTag) {
      clear(menuTag);
      renderMenu();
      this.setupMenuHandlers();
    }

    // Setup account modal after UI is rendered
    // Add a small delay to ensure DOM elements are available
    setTimeout(() => {
      this.setupAccountModal();
    }, 100);
  }

  handleSignedOut() {
    // Emit custom event for the new UI handler
    const logoutEvent = new CustomEvent("userLoggedOut");
    document.dispatchEvent(logoutEvent);

    // Legacy UI updates for existing functionality
    const labelWelcome = document.querySelector(".welcome");
    const containerApp = document.querySelector(".app");
    const btnLogout = document.querySelector(".logout__btn");
    const btnHelp = document.querySelector("#help-btn");
    const btnAccount = document.querySelector("#account-btn");
    const loginUser = document.querySelector(".login__input--user");
    const loginPwd = document.querySelector(".login__input--pin");
    const btnLogin = document.querySelector(".login__btn");
    const menuTag = document.querySelector(".menu-tag");

    // Legacy updates for existing code compatibility
    if (loginUser) loginUser.classList.remove("hidden");
    if (loginPwd) loginPwd.classList.remove("hidden");
    if (btnLogin) btnLogin.classList.remove("hidden");
    if (btnLogout) btnLogout.classList.add("hidden");
    if (btnHelp) btnHelp.classList.add("hidden");
    if (btnAccount) btnAccount.classList.add("hidden");
    if (containerApp) containerApp.style.opacity = 0;
    if (labelWelcome) labelWelcome.textContent = "Accedi per iniziare";

    // Reset menu
    if (menuTag) {
      clear(menuTag);
      menuTag.classList.remove("no-pointer-events");
    }

    this.currentAccount = null;
    this.logoutStatusCheck = false;
  }

  renderLogoutWindow(message) {
    // Remove any existing logout modal
    const existingModal = document.getElementById("logoutConfirmModal");
    if (existingModal) {
      existingModal.remove();
    }

    // Create Bootstrap modal for logout confirmation using crop controller pattern
    const modalHTML = `
      <div class="modal fade show" style="display: block; background-color: rgba(0,0,0,0.5);" id="logoutConfirmModal">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header bg-warning text-dark">
              <h5 class="modal-title">
                <i class="bi bi-exclamation-triangle me-2"></i>
                Conferma Logout
              </h5>
            </div>
            <div class="modal-body">
              <p class="mb-0">${message}</p>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" id="cancelLogoutBtn">
                <i class="bi bi-x-circle me-1"></i>
                Annulla
              </button>
              <button type="button" class="btn btn-danger" id="confirmLogoutBtn">
                <i class="bi bi-box-arrow-right me-1"></i>
                Confermo
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    // Add modal to body using container approach like crop controller
    const modalContainer = document.createElement("div");
    modalContainer.innerHTML = modalHTML;
    document.body.appendChild(modalContainer);

    const modal = modalContainer.querySelector("#logoutConfirmModal");
    const confirmBtn = modal.querySelector("#confirmLogoutBtn");
    const cancelBtn = modal.querySelector("#cancelLogoutBtn");

    // Handle confirm logout
    confirmBtn.addEventListener("click", async () => {
      // Proceed with logout
      const { error } = await authService.signOut();
      if (error) console.error("Logout error:", error.message);

      // Remove modal
      document.body.removeChild(modalContainer);
    });

    // Handle cancel logout
    cancelBtn.addEventListener("click", () => {
      document.body.removeChild(modalContainer);
    });
  }

  setupMenuHandlers() {
    const menuTag = document.querySelector(".menu-tag");
    if (!menuTag) return;

    // Remove any existing event listeners
    const newMenuTag = menuTag.cloneNode(true);
    menuTag.parentNode.replaceChild(newMenuTag, menuTag);

    newMenuTag.addEventListener("click", async (e) => {
      const currentBtn = e.target.closest(".menu__btn");
      if (!currentBtn) return;

      // Set logout status based on which menu item is clicked
      this.logoutStatusCheck = currentBtn.classList.contains("new-plan_btn");

      // Handle menu button clicks
      if (currentBtn.classList.contains("new-plan_btn")) {
        console.log("newPlan");
        await controlNewPlan(currentBtn);
      }
      if (currentBtn.classList.contains("new-plan-test_btn")) {
        console.log("newPlanTest");
        await controlNewPlanTest(currentBtn);
      }
      if (currentBtn.classList.contains("open-plan_btn")) {
        console.log("openPlan");
        await controlOpenPlan(currentBtn);
      }
      if (currentBtn.classList.contains("company_btn")) {
        await controlCompany(currentBtn, this.currentAccount);
      }
      if (currentBtn.classList.contains("products_btn")) {
        await controlProduct(currentBtn, this.currentAccount);
      }
      if (currentBtn.classList.contains("fertiProducts_btn")) {
        await controlFertiProduct(currentBtn, this.currentAccount);
      }
      if (currentBtn.classList.contains("crops_btn")) {
        await controlCrop(currentBtn, this.currentAccount);
      }
      if (currentBtn.classList.contains("sectors_btn")) {
        await controlSector(currentBtn, this.currentAccount);
      }
    });
  }

  async checkInitialAuthState() {
    try {
      const { user, userProfile } = await authService.getCurrentUserWithRole();
      if (user) {
        await this.handleSignedIn(user);
      } else {
        this.handleSignedOut();
      }
    } catch (error) {
      console.error("Error checking initial auth state:", error);
      this.handleSignedOut();
    } finally {
      // Dispatch event to hide the loader
      document.dispatchEvent(new Event("authStateDetermined"));
    }
  }

  setupAccountModal() {
    const accountBtn = document.querySelector("#account-btn");
    const updateAccountWindow = document.querySelector(
      ".update-account-window"
    );
    const closeModalBtn = document.querySelector(".btn--close-modal-account");
    const uploadAccountForm = document.querySelector(".upload-account");
    const overlay = document.querySelector(".overlay");

    if (
      !accountBtn ||
      !updateAccountWindow ||
      !closeModalBtn ||
      !uploadAccountForm ||
      !overlay
    ) {
      console.error("Required DOM elements not found for account modal");
      console.log("accountBtn:", accountBtn);
      console.log("updateAccountWindow:", updateAccountWindow);
      console.log("closeModalBtn:", closeModalBtn);
      console.log("uploadAccountForm:", uploadAccountForm);
      console.log("overlay:", overlay);
      return;
    }

    // Remove any existing listeners
    const newAccountBtn = accountBtn.cloneNode(true);
    accountBtn.parentNode.replaceChild(newAccountBtn, accountBtn);

    // Open modal handler
    newAccountBtn.addEventListener("click", async () => {
      const { user, userProfile } = await authService.getCurrentUserWithRole();
      if (!user || !userProfile) return;

      // Populate form with current values
      document.getElementById("account-username").value =
        userProfile.display_name || "";
      document.getElementById("account-phone").value =
        userProfile.phone_number || "";
      document.getElementById("account-description").value =
        userProfile.description || "";

      updateAccountWindow.classList.remove("hidden");
      overlay.classList.remove("hidden");
    });

    // Close modal handler
    const closeModal = () => {
      updateAccountWindow.classList.add("hidden");
      overlay.classList.add("hidden");
    };

    closeModalBtn.addEventListener("click", closeModal);
    overlay.addEventListener("click", closeModal);

    // Form submission handler
    uploadAccountForm.addEventListener("submit", async (e) => {
      e.preventDefault();

      const updates = {
        display_name: document.getElementById("account-username").value,
        phone_number: document.getElementById("account-phone").value,
        description: document.getElementById("account-description").value,
      };

      try {
        const { data, error } = await authService.updateUserProfile(
          this.currentAccount.id,
          updates
        );

        if (error) throw error;

        // Update current account info
        this.currentAccount = {
          ...this.currentAccount,
          ...updates,
        };

        // Update welcome message
        const labelWelcome = document.querySelector(".welcome");
        if (labelWelcome) {
          labelWelcome.textContent = `Buon lavoro, ${
            updates.username || this.currentAccount.email
          }`;
        }

        // Show success message
        const successToast = new bootstrap.Toast(
          document.getElementById("successToast")
        );
        document.getElementById("successMessage").textContent =
          "Account aggiornato con successo";
        successToast.show();

        closeModal();
      } catch (error) {
        console.error("Error updating profile:", error);
        // Show error message
        const errorToast = new bootstrap.Toast(
          document.getElementById("errorToast")
        );
        document.getElementById("errorMessage").textContent =
          "Errore durante l'aggiornamento dell'account";
        errorToast.show();
      }
    });
  }
}

export const auth = new Auth();

// Modern Authentication Handler for AgriFertify
class AuthHandler {
  constructor() {
    this.errorToast = null;
    this.initializeElements();
    this.bindEvents();
    this.setupAuthStateListener();
  }

  initializeElements() {
    // Get form elements first
    this.loginSection = document.getElementById("loginSection");
    this.loginForm = document.getElementById("login_fields");
    this.emailInput = document.querySelector(".login__input--user");
    this.passwordInput = document.querySelector(".login__input--pin");
    this.logoutBtn = document.getElementById("logout-btn");
    this.welcomeText = document.getElementById("welcome-text");
    this.togglePasswordBtn = document.getElementById("togglePassword");
    this.errorMessage = document.getElementById("errorMessage");
    this.appContainer = document.querySelector(".app");

    // Initialize Bootstrap toast
    const toastElement = document.getElementById("errorToast");
    if (toastElement) {
      this.errorToast = new bootstrap.Toast(toastElement, {
        autohide: true,
        delay: 5000,
      });
    }
  }

  bindEvents() {
    // Login form submission
    if (this.loginForm) {
      this.loginForm.addEventListener("submit", (e) => this.handleLogin(e));
    }

    // Logout button - use the existing auth system
    if (this.logoutBtn) {
      this.logoutBtn.addEventListener("click", async () => {
        await auth.signOut();
      });
    }

    // Password toggle functionality - prevent double binding
    if (this.togglePasswordBtn && this.passwordInput) {
      // Remove any existing event listeners
      this.togglePasswordBtn.replaceWith(
        this.togglePasswordBtn.cloneNode(true)
      );
      this.togglePasswordBtn = document.getElementById("togglePassword");

      this.togglePasswordBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        console.log("Password toggle clicked - preventing double trigger");
        this.togglePassword();
      });
    }
  }

  setupAuthStateListener() {
    // Listen for custom auth events from the existing system
    document.addEventListener("userLoggedIn", (e) => {
      this.handleUserLoggedIn(e.detail.user);
    });

    document.addEventListener("userLoggedOut", () => {
      this.handleUserLoggedOut();
    });

    // Check initial auth state using the existing auth instance
    auth.checkInitialAuthState();
  }

  async handleLogin(event) {
    event.preventDefault();

    const email = this.emailInput.value.trim();
    const password = this.passwordInput.value;

    // Basic validation
    if (!email || !password) {
      this.showError("Inserisci email e password");
      return;
    }

    if (!this.isValidEmail(email)) {
      this.showError("Inserisci un indirizzo email valido");
      return;
    }

    // Disable form during submission
    this.setFormLoading(true);

    try {
      // Use the existing auth system
      const result = await auth.signIn(email, password);

      console.log("Login result:", result); // Debug log

      if (result.success) {
        // Auth state change will be handled by the existing system
        this.resetForm();
      } else {
        // Show error for failed login
        const errorMsg = result.error || "Email o password non corretti";
        console.log("Login failed, showing error:", errorMsg); // Debug log
        this.showError(errorMsg);
      }
    } catch (error) {
      console.error("Login error caught:", error);
      this.showError("Errore durante il login. Riprova più tardi.");
    } finally {
      this.setFormLoading(false);
    }
  }

  handleUserLoggedIn(user) {
    // Hide login section
    if (this.loginSection) {
      this.loginSection.classList.add("hidden");
    }

    // Show app container
    if (this.appContainer) {
      this.appContainer.classList.remove("hidden");
      this.appContainer.style.opacity = "1";
    }

    // Show logout button
    if (this.logoutBtn) {
      this.logoutBtn.classList.remove("hidden");
    }

    // Reset form
    this.resetForm();
  }

  handleUserLoggedOut() {
    // Show login section
    if (this.loginSection) {
      this.loginSection.classList.remove("hidden");
    }

    // Hide app container
    if (this.appContainer) {
      this.appContainer.classList.add("hidden");
      this.appContainer.style.opacity = "0";
    }

    // Hide logout button
    if (this.logoutBtn) {
      this.logoutBtn.classList.add("hidden");
    }

    // Reset form
    this.resetForm();
  }

  showError(message) {
    console.log("Showing error:", message); // Debug log

    if (this.errorMessage) {
      this.errorMessage.textContent = message;
    }

    if (this.errorToast) {
      try {
        this.errorToast.show();
        console.log("Toast shown"); // Debug log
      } catch (error) {
        console.error("Error showing toast:", error);
        // Fallback: show alert if toast fails
        alert(message);
      }
    } else {
      console.log("No toast available, using alert"); // Debug log
      // Fallback: show alert if toast is not available
      alert(message);
    }
  }

  togglePassword() {
    if (!this.passwordInput || !this.togglePasswordBtn) {
      console.log("Password toggle elements not found");
      return;
    }

    // Add a small delay to prevent rapid double-clicks
    if (this.togglePasswordBtn.disabled) {
      console.log("Toggle already in progress, ignoring");
      return;
    }

    // Temporarily disable button to prevent double-clicks
    this.togglePasswordBtn.disabled = true;

    console.log("Toggling password visibility"); // Debug log

    const currentType = this.passwordInput.getAttribute("type");
    const newType = currentType === "password" ? "text" : "password";

    console.log("Current type:", currentType, "New type:", newType); // Debug log

    this.passwordInput.setAttribute("type", newType);

    const icon = this.togglePasswordBtn.querySelector("i");
    if (icon) {
      const newIconClass =
        newType === "password" ? "bi bi-eye" : "bi bi-eye-slash";
      console.log("Setting icon class to:", newIconClass); // Debug log
      icon.className = newIconClass;
    } else {
      console.log("Icon element not found in toggle button");
    }

    // Re-enable button after a short delay
    setTimeout(() => {
      this.togglePasswordBtn.disabled = false;
    }, 100);
  }

  setFormLoading(loading) {
    const submitBtn = this.loginForm.querySelector('button[type="submit"]');
    const inputs = this.loginForm.querySelectorAll("input");

    if (loading) {
      submitBtn.disabled = true;
      submitBtn.innerHTML = `
        <span class="spinner-border spinner-border-sm me-2" role="status"></span>
        Accesso in corso...
      `;
      inputs.forEach((input) => (input.disabled = true));
    } else {
      submitBtn.disabled = false;
      submitBtn.innerHTML = `
        <i class="bi bi-box-arrow-in-right me-1"></i>
        Accedi
      `;
      inputs.forEach((input) => (input.disabled = false));
    }
  }

  resetForm() {
    this.loginForm.reset();
    this.setFormLoading(false);

    // Reset password visibility
    this.passwordInput.setAttribute("type", "password");
    this.togglePasswordBtn.querySelector("i").className = "bi bi-eye";

    // Clear any validation states
    this.loginForm.querySelectorAll(".is-invalid").forEach((el) => {
      el.classList.remove("is-invalid");
    });
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}

// Initialize authentication handler when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  const authHandler = new AuthHandler();

  // Add temporary test buttons (remove after testing)
  // setTimeout(() => {
  //   // Test login error button
  //   const testLoginErrorBtn = document.createElement("button");
  //   testLoginErrorBtn.textContent = "Test Login Error";
  //   testLoginErrorBtn.className = "btn btn-danger btn-sm position-fixed";
  //   testLoginErrorBtn.style.bottom = "60px";
  //   testLoginErrorBtn.style.left = "20px";
  //   testLoginErrorBtn.style.zIndex = "9999";
  //   testLoginErrorBtn.onclick = async () => {
  //     // Simulate a failed login attempt
  //     const fakeResult = { success: false, error: "Invalid login credentials" };
  //     console.log("Simulating login error:", fakeResult);
  //     authHandler.showError(fakeResult.error);
  //   };
  //   document.body.appendChild(testLoginErrorBtn);
  // }, 1000);
});
